import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { AnalyticsListener } from "@/components/AnalyticsListener";

import Home from "@/pages/Home";
import Listing from "@/pages/Listing";
import ArticleDetail from "@/pages/ArticleDetail";
import ComparisonDetail from "@/pages/ComparisonDetail";
import About from "@/pages/About";
import ShareYourStack from "@/pages/ShareYourStack";
import Legal from "@/pages/Legal";

import AdminLogin from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";
import ArticleEditor from "@/pages/admin/ArticleEditor";
import AffiliateManager from "@/pages/admin/AffiliateManager";
import FeedbackList from "@/pages/admin/FeedbackList";
import Settings from "@/pages/admin/Settings";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center font-mono-pm text-sm text-stone-500">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

const Site = ({ children }) => <Layout>{children}</Layout>;

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <AnalyticsListener />
          <Routes>
            {/* public */}
            <Route path="/" element={<Site><Home /></Site>} />
            <Route path="/software" element={<Site><Listing key="software" type="software_review" title="Software Reviews" tagline="Property management platforms, put to The PM Test." /></Site>} />
            <Route path="/tools-tech" element={<Site><Listing key="tools" type="product_review" title="Tools & Tech" tagline="Smart locks, cameras, leak detection and the hardware that runs properties." /></Site>} />
            <Route path="/comparisons" element={<Site><Listing key="comp" type="comparison" title="Head-to-Head Comparisons" tagline="Side-by-side, real-world matchups." /></Site>} />
            <Route path="/guides" element={<Site><Listing key="guides" types={["buying_guide", "guide"]} title="Guides & Buying Advice" tagline="Best-of roundups and practical property management guides." /></Site>} />
            <Route path="/about" element={<Site><About /></Site>} />
            <Route path="/share-your-stack" element={<Site><ShareYourStack /></Site>} />
            <Route path="/reviews/:slug" element={<Site><ArticleDetail /></Site>} />
            <Route path="/comparisons/:slug" element={<Site><ComparisonDetail /></Site>} />
            <Route path="/privacy-policy" element={<Site><Legal page="privacy" /></Site>} />
            <Route path="/terms-of-use" element={<Site><Legal page="terms" /></Site>} />
            <Route path="/affiliate-disclosure" element={<Site><Legal page="affiliate" /></Site>} />
            <Route path="/editorial-policy" element={<Site><Legal page="editorial" /></Site>} />

            {/* admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin/articles/new" element={<ProtectedRoute><ArticleEditor /></ProtectedRoute>} />
            <Route path="/admin/articles/:id" element={<ProtectedRoute><ArticleEditor /></ProtectedRoute>} />
            <Route path="/admin/affiliates" element={<ProtectedRoute><AffiliateManager /></ProtectedRoute>} />
            <Route path="/admin/feedback" element={<ProtectedRoute><FeedbackList /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
